import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import './Carousel.css';

function InstagramPostItem({ post, onClick }) {
  const renderCarousel = () => {
    if (!post.mediaUrls || post.mediaUrls.length === 0) {
      return (
        <div className="flex items-center justify-center w-full h-full bg-gray-100 text-center">
          <span className="text-sm text-gray-500">Image coming soon…</span>
        </div>
      );
    }

    if (post.mediaUrls.length === 1) {
      return renderMedia();
    }

    return (
      <div className="relative w-full h-full">
        <Swiper
          modules={[Pagination]}
          pagination={{
            clickable: true,
            dynamicBullets: true,
          }}
          slidesPerView={1}
          className="w-full h-full"
        >
          {post.mediaUrls.map((url, index) => (
            <SwiperSlide key={index} className="w-full h-full">
              <img
                src={url}
                alt={`${post.caption || 'Instagram post'} #${index + 1}`}
                className="w-full h-full object-cover"
              />
            </SwiperSlide>
          ))}
        </Swiper>
        {/* Instagram-style carousel indicator */}
        <div className="absolute top-2 right-2 bg-gray-200 bg-opacity-70 px-2 py-1 rounded-full text-xs font-medium">
          <span aria-label="Multiple images">1/{post.mediaUrls.length}</span>
        </div>
      </div>
    );
  };

  const renderMedia = () => {
    // Use thumbnailUrl for grid display, fallback to existing logic
    const displayUrl = post.thumbnailUrl || post.mediaUrl;
    
    if (!displayUrl) {
      return (
        <div className="flex items-center justify-center w-full h-full bg-gray-100 text-center">
          <span className="text-sm text-gray-500">Image coming soon…</span>
        </div>
      );
    }

    // If it's video-only (no separate image), render as video in grid
    if ((post.hasVideo && !post.hasImage) || (post.isVideo && !post.hasImage)) {
      return (
        <div className="relative w-full h-full">
          <video
            src={displayUrl}
            className="w-full h-full object-cover"
            preload="metadata"
            playsInline
            muted
            aria-label={post.caption || 'Instagram video post'}
          >
            <track kind="captions" src="" label="English" />
          </video>
        </div>
      );
    }

    // For image or image+video cases, show as image in grid
    return (
      <img
        src={displayUrl}
        alt={post.caption || 'Instagram image post'}
        className="w-full h-full object-cover"
      />
    );
  };

  return (
    <button
      className="relative border border-gray-200 cursor-pointer text-left w-full p-0 m-0 bg-transparent"
      onClick={() => onClick(post)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onClick(post);
        }
      }}
      aria-label={`View post: ${post.name}`}
    >
      {/* Instagram-style Post Header */}
      <div className="bg-gray-200 p-2 post-heading-container">
        <div className="flex items-center justify-between">
          <div className="overflow-hidden">
            <div className="font-medium text-sm truncate pr-2">{post.name}</div>
            <div className="text-xs text-gray-500 truncate">
              {post.date} — {post.postType}
            </div>
          </div>
          {post.approved && (
            <div className="flex-shrink-0">
              <span className="text-green-500 text-lg" aria-label="Approved">✔</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Media Container with Fixed Aspect Ratio */}
      <div className="aspect-[4/5] relative overflow-hidden">
        {post.hasMultipleImages ? renderCarousel() : renderMedia()}
        {(post.hasVideo || post.isVideo) && (
          <div className="absolute top-2 right-2 bg-gray-200 bg-opacity-70 rounded-full p-1 shadow" aria-hidden="true">
            <svg className="w-4 h-4 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" fill="none" />
            </svg>
          </div>
        )}
      </div>
    </button>
  );
}

export default InstagramPostItem;
